/**
 * FinderPreview Component
 *
 * Detail preview panel for selected item.
 */

import type { ReactNode } from 'react'
import { ContentItem } from './FinderWindow'
import './finder.css'

interface FinderPreviewProps {
  item: ContentItem | null
}

// Get display name for item
const getDisplayName = (item: ContentItem): string => {
  return item.name || item.company || item.title || item.slug
}

// Helper to safely get string value
const getString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.length > 0) return value
  if (typeof value === 'number') return String(value)
  return null
}

export function FinderPreview({ item }: FinderPreviewProps) {
  if (!item) {
    return (
      <div className="finder-preview finder-preview--empty">
        <div className="finder-preview__placeholder">
          <span className="finder-preview__placeholder-icon">📄</span>
          <p>Select an item to preview</p>
        </div>
      </div>
    )
  }

  const displayName = getDisplayName(item)
  const hasImage = item.icon?.includes('.') || item.thumbnail

  // Extract known properties with type assertions
  const location = getString(item.location)
  const start = getString(item.start)
  const end = getString(item.end)
  const type = getString(item.type)
  const proficiency = getString(item.proficiency)
  const years = getString(item.years)
  const teamSize = getString(item.team_size)

  // Render details section
  const renderDetails = (): ReactNode => {
    const details: ReactNode[] = []

    if (location) {
      details.push(
        <div key="location" className="finder-preview__detail">
          <span className="finder-preview__detail-label">Location</span>
          <span className="finder-preview__detail-value">{location}</span>
        </div>
      )
    }

    if (start) {
      details.push(
        <div key="period" className="finder-preview__detail">
          <span className="finder-preview__detail-label">Period</span>
          <span className="finder-preview__detail-value">
            {start} - {end || 'Present'}
          </span>
        </div>
      )
    }

    if (type) {
      details.push(
        <div key="type" className="finder-preview__detail">
          <span className="finder-preview__detail-label">Type</span>
          <span className="finder-preview__detail-value">{type}</span>
        </div>
      )
    }

    if (proficiency) {
      details.push(
        <div key="proficiency" className="finder-preview__detail">
          <span className="finder-preview__detail-label">Proficiency</span>
          <span className="finder-preview__detail-value">{proficiency}</span>
        </div>
      )
    }

    if (years) {
      details.push(
        <div key="years" className="finder-preview__detail">
          <span className="finder-preview__detail-label">Experience</span>
          <span className="finder-preview__detail-value">{years} years</span>
        </div>
      )
    }

    if (teamSize) {
      details.push(
        <div key="team" className="finder-preview__detail">
          <span className="finder-preview__detail-label">Team Size</span>
          <span className="finder-preview__detail-value">{teamSize}</span>
        </div>
      )
    }

    if (details.length === 0) return null

    return <div className="finder-preview__details">{details}</div>
  }

  return (
    <div className="finder-preview">
      {/* Preview header with icon */}
      <div className="finder-preview__header">
        {hasImage ? (
          <img
            src={item.thumbnail || `/icons/${item.icon}`}
            alt=""
            className="finder-preview__icon"
          />
        ) : (
          <span className="finder-preview__icon-emoji">
            {item.icon || '📄'}
          </span>
        )}
        <h3 className="finder-preview__title">{displayName}</h3>
        {item.role && (
          <p className="finder-preview__subtitle">{item.role}</p>
        )}
      </div>

      {/* Preview details - using explicit render function */}
      {renderDetails()}

      {/* Tags */}
      {item.tags && item.tags.length > 0 ? (
        <div className="finder-preview__tags">
          {item.tags.map((tag) => (
            <span key={tag} className="finder-preview__tag">
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      {/* Highlights */}
      {Array.isArray(item.highlights) && item.highlights.length > 0 ? (
        <div className="finder-preview__highlights">
          <h4 className="finder-preview__section-title">Highlights</h4>
          <ul className="finder-preview__highlight-list">
            {(item.highlights as string[]).slice(0, 3).map((highlight, index) => (
              <li key={index}>{highlight}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Content preview */}
      {item.content ? (
        <div className="finder-preview__content">
          <p>{item.content.slice(0, 200)}...</p>
        </div>
      ) : null}
    </div>
  )
}

export default FinderPreview
